export const CHAT_ATTACHMENT_MAX_TEXT_BYTES = 256 * 1024
export const CHAT_ATTACHMENT_MAX_IMAGE_BYTES = (15 * 1024 * 1024) / 4
export const CHAT_ATTACHMENT_MAX_PDF_BYTES = (9 * 1024 * 1024) / 2
export const CHAT_ATTACHMENT_MAX_FILE_BYTES = CHAT_ATTACHMENT_MAX_PDF_BYTES
export const CHAT_ATTACHMENT_MAX_TOTAL_BYTES = 16 * 1024 * 1024
export const CHAT_ATTACHMENT_MULTIPART_OVERHEAD_BYTES = 1 * 1024 * 1024
export const CHAT_ATTACHMENT_MAX_BODY_BYTES =
  CHAT_ATTACHMENT_MAX_TOTAL_BYTES + CHAT_ATTACHMENT_MULTIPART_OVERHEAD_BYTES
export const CHAT_ATTACHMENT_PROXY_MEMORY_BYTES = 1 * 1024 * 1024
export const CHAT_ATTACHMENT_MAX_CONCURRENT_TRANSFERS = 4
export const CHAT_ATTACHMENT_TRANSFER_TIMEOUT_MS = 5 * 60 * 1000

export type ChatAttachmentFileClass = 'native_chat' | 'ingest_handoff'
export type ChatAttachmentContentKind = 'text' | 'image' | 'pdf' | 'ingest_handoff'

interface NativeChatAttachmentPolicyEntry {
  canonicalMimeType: string
  fileClass: 'native_chat'
  contentKind: Exclude<ChatAttachmentContentKind, 'ingest_handoff'>
  maxBytes: number
}

interface IngestHandoffAttachmentPolicyEntry {
  canonicalMimeType: string
  fileClass: 'ingest_handoff'
  contentKind: 'ingest_handoff'
}

export type ChatAttachmentPolicyEntry =
  | NativeChatAttachmentPolicyEntry
  | IngestHandoffAttachmentPolicyEntry

export const CHAT_ATTACHMENT_POLICY_BY_EXTENSION: Readonly<
  Record<string, ChatAttachmentPolicyEntry>
> = {
  '.png': {
    canonicalMimeType: 'image/png',
    fileClass: 'native_chat',
    contentKind: 'image',
    maxBytes: CHAT_ATTACHMENT_MAX_IMAGE_BYTES
  },
  '.jpg': {
    canonicalMimeType: 'image/jpeg',
    fileClass: 'native_chat',
    contentKind: 'image',
    maxBytes: CHAT_ATTACHMENT_MAX_IMAGE_BYTES
  },
  '.jpeg': {
    canonicalMimeType: 'image/jpeg',
    fileClass: 'native_chat',
    contentKind: 'image',
    maxBytes: CHAT_ATTACHMENT_MAX_IMAGE_BYTES
  },
  '.gif': {
    canonicalMimeType: 'image/gif',
    fileClass: 'native_chat',
    contentKind: 'image',
    maxBytes: CHAT_ATTACHMENT_MAX_IMAGE_BYTES
  },
  '.webp': {
    canonicalMimeType: 'image/webp',
    fileClass: 'native_chat',
    contentKind: 'image',
    maxBytes: CHAT_ATTACHMENT_MAX_IMAGE_BYTES
  },
  '.pdf': {
    canonicalMimeType: 'application/pdf',
    fileClass: 'native_chat',
    contentKind: 'pdf',
    maxBytes: CHAT_ATTACHMENT_MAX_PDF_BYTES
  },
  '.txt': {
    canonicalMimeType: 'text/plain',
    fileClass: 'native_chat',
    contentKind: 'text',
    maxBytes: CHAT_ATTACHMENT_MAX_TEXT_BYTES
  },
  '.md': {
    canonicalMimeType: 'text/markdown',
    fileClass: 'native_chat',
    contentKind: 'text',
    maxBytes: CHAT_ATTACHMENT_MAX_TEXT_BYTES
  },
  '.csv': {
    canonicalMimeType: 'text/csv',
    fileClass: 'native_chat',
    contentKind: 'text',
    maxBytes: CHAT_ATTACHMENT_MAX_TEXT_BYTES
  },
  '.json': {
    canonicalMimeType: 'application/json',
    fileClass: 'native_chat',
    contentKind: 'text',
    maxBytes: CHAT_ATTACHMENT_MAX_TEXT_BYTES
  },
  '.doc': {
    canonicalMimeType: 'application/msword',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  },
  '.docx': {
    canonicalMimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  },
  '.xls': {
    canonicalMimeType: 'application/vnd.ms-excel',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  },
  '.xlsx': {
    canonicalMimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  },
  '.bak': {
    canonicalMimeType: 'application/octet-stream',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  },
  '.gwbk': {
    canonicalMimeType: 'application/zip',
    fileClass: 'ingest_handoff',
    contentKind: 'ingest_handoff'
  }
}

export const CHAT_ATTACHMENT_ACCEPT = Object.entries(CHAT_ATTACHMENT_POLICY_BY_EXTENSION)
  .filter(([, policy]) => policy.fileClass === 'native_chat')
  .map(([extension]) => extension)
  .join(',')

export const CHAT_ATTACHMENT_MIME_ALIASES: Readonly<Record<string, string>> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'application/x-pdf': 'application/pdf',
  'application/x-zip-compressed': 'application/zip',
  'text/x-markdown': 'text/markdown',
  'text/json': 'application/json'
}

export type ChatAttachmentErrorCode =
  | 'ATTACHMENT_EMPTY'
  | 'ATTACHMENT_FILE_TOO_LARGE'
  | 'ATTACHMENT_TEXT_TOO_LARGE'
  | 'ATTACHMENT_IMAGE_TOO_LARGE'
  | 'ATTACHMENT_PDF_TOO_LARGE'
  | 'ATTACHMENT_TOTAL_TOO_LARGE'
  | 'ATTACHMENT_TYPE_UNSUPPORTED'
  | 'ATTACHMENT_MIME_MISMATCH'
  | 'ATTACHMENT_MULTIPART_TOO_LARGE'
  | 'ATTACHMENT_TRANSFER_TIMEOUT'
  | 'ATTACHMENT_INGEST_REQUIRED'
  | 'ATTACHMENT_CLIENT_ID_REQUIRED'
  | 'ATTACHMENT_CLIENT_ID_DUPLICATE'
  | 'ATTACHMENT_STORAGE_UNAVAILABLE'
  | 'ATTACHMENT_UPLOAD_TIMEOUT'
  | 'ATTACHMENT_AUTHORIZATION_FAILED'
  | 'ATTACHMENT_OBJECT_WRITE_FAILED'
  | 'ATTACHMENT_METADATA_WRITE_FAILED'
  | 'ATTACHMENT_IDEMPOTENCY_CONFLICT'
  | 'ATTACHMENT_UPLOAD_IN_PROGRESS'
  | 'ATTACHMENT_DRAFT_EXPIRED'
  | 'ATTACHMENT_CLAIM_FAILED'

export type ChatAttachmentErrorAction = 'remove' | 'retry' | 'use_ingest'

export interface ChatAttachmentFailure {
  index: number | null
  /** Stable browser identity when the failure occurred after multipart parsing. */
  clientUploadId?: string
  code: ChatAttachmentErrorCode
  message: string
  retryable: boolean
  action: ChatAttachmentErrorAction
}

export interface ChatAttachmentErrorResponse {
  error: 'CHAT_ATTACHMENT_VALIDATION_FAILED'
  message: string
  attachments: ChatAttachmentFailure[]
}

const FAILURE_DETAILS: Record<
  ChatAttachmentErrorCode,
  Pick<ChatAttachmentFailure, 'message' | 'retryable' | 'action'>
> = {
  ATTACHMENT_EMPTY: {
    message: 'This file is empty. Remove it and choose a non-empty file.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_FILE_TOO_LARGE: {
    message: 'This file is too large to read in chat.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_TEXT_TOO_LARGE: {
    message: 'Text attachments must total 256 KiB or less so chat can read all of their content.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_IMAGE_TOO_LARGE: {
    message: 'Images must be 3.75 MiB or smaller to be read in chat.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_PDF_TOO_LARGE: {
    message: 'PDFs must be 4.5 MiB or smaller to be read in chat.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_TOTAL_TOO_LARGE: {
    message: 'Attachments must total 16 MiB or less to be read in chat.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_TYPE_UNSUPPORTED: {
    message: 'This file type is not supported in chat.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_MIME_MISMATCH: {
    message: 'The file content does not match its name or declared MIME type.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_MULTIPART_TOO_LARGE: {
    message: 'The multipart request exceeds the 17 MiB chat body limit.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_TRANSFER_TIMEOUT: {
    message: 'The attachment transfer timed out. Retry the upload.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_INGEST_REQUIRED: {
    message: 'Office and backup files must be processed with Ingest instead of chat.',
    retryable: false,
    action: 'use_ingest'
  },
  ATTACHMENT_CLIENT_ID_REQUIRED: {
    message: 'The attachment upload identity is missing or invalid. Remove and re-stage the file.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_CLIENT_ID_DUPLICATE: {
    message:
      'Each attachment must have a unique upload identity. Remove and re-stage the duplicate file.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_STORAGE_UNAVAILABLE: {
    message: 'Attachment storage is unavailable. Try again.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_UPLOAD_TIMEOUT: {
    message: 'The attachment upload timed out. Try again.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_AUTHORIZATION_FAILED: {
    message: 'You do not have permission to attach files to this conversation.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_OBJECT_WRITE_FAILED: {
    message: 'The attachment could not be stored. Try again.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_METADATA_WRITE_FAILED: {
    message: 'The attachment record could not be saved. Try again.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_IDEMPOTENCY_CONFLICT: {
    message: 'This staged file no longer matches its upload identity. Remove and re-stage it.',
    retryable: false,
    action: 'remove'
  },
  ATTACHMENT_UPLOAD_IN_PROGRESS: {
    message: 'This attachment is still uploading. Try again shortly.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_DRAFT_EXPIRED: {
    message: 'The attachment upload expired before it could be attached. Try again.',
    retryable: true,
    action: 'retry'
  },
  ATTACHMENT_CLAIM_FAILED: {
    message: 'The uploaded attachments could not be attached to this chat. Try again.',
    retryable: true,
    action: 'retry'
  }
}

export function chatAttachmentFailure(
  code: ChatAttachmentErrorCode,
  index: number | null
): ChatAttachmentFailure {
  return { index, code, ...FAILURE_DETAILS[code] }
}

export function chatAttachmentExtension(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot === -1 ? '' : filename.slice(dot).toLowerCase()
}

export function chatAttachmentPolicyForFilename(
  filename: string
): ChatAttachmentPolicyEntry | null {
  return CHAT_ATTACHMENT_POLICY_BY_EXTENSION[chatAttachmentExtension(filename)] ?? null
}

export function normalizeChatAttachmentMime(mimeType: string): string | null {
  const normalized = mimeType.split(';', 1)[0]?.trim().toLowerCase() ?? ''
  if (normalized === '' || normalized === 'application/octet-stream') return null
  return CHAT_ATTACHMENT_MIME_ALIASES[normalized] ?? normalized
}

export function validateChatAttachmentMetadata(
  file: { name: string; size: number },
  index: number
): ChatAttachmentFailure | null {
  const policy = chatAttachmentPolicyForFilename(file.name)
  if (file.size === 0) return chatAttachmentFailure('ATTACHMENT_EMPTY', index)
  if (policy === null) return chatAttachmentFailure('ATTACHMENT_TYPE_UNSUPPORTED', index)
  if (policy.fileClass === 'ingest_handoff') {
    return chatAttachmentFailure('ATTACHMENT_INGEST_REQUIRED', index)
  }
  if (file.size > policy.maxBytes) {
    const code: ChatAttachmentErrorCode =
      policy.contentKind === 'text'
        ? 'ATTACHMENT_TEXT_TOO_LARGE'
        : policy.contentKind === 'image'
          ? 'ATTACHMENT_IMAGE_TOO_LARGE'
          : 'ATTACHMENT_PDF_TOO_LARGE'
    return chatAttachmentFailure(code, index)
  }
  return null
}

export function validateChatAttachmentTotalBytes(totalBytes: number): ChatAttachmentFailure | null {
  return totalBytes > CHAT_ATTACHMENT_MAX_TOTAL_BYTES
    ? chatAttachmentFailure('ATTACHMENT_TOTAL_TOO_LARGE', null)
    : null
}

export function validateChatAttachmentTextBytes(textBytes: number): ChatAttachmentFailure | null {
  return textBytes > CHAT_ATTACHMENT_MAX_TEXT_BYTES
    ? chatAttachmentFailure('ATTACHMENT_TEXT_TOO_LARGE', null)
    : null
}
